// ── Collateral Management — Insights ──
// Portfolio roll-up of AR/AP collateral flags, with per-account drill-down.
//
// Everything shown here is scored against each borrower's OWN trailing history
// (see dbo.vCollateralInsightFlags). A metric that is structurally high for a
// borrower does not flag; a metric that moves off that borrower's baseline does.
//
// Tiles are gated on coverage: an account with no AR credits in its file
// shows "not reported" for dilution rather than a zero, because a zero and a
// missing feed look identical on a dashboard and mean opposite things.
//
// UNDRAWN LINES ARE SET ASIDE, NOT HIDDEN. A borrower whose revolving line is
// at $0 today (BS Loan Balance, live, from getLoans — the same figure the
// Collateral Portfolio page shows) has nothing advanced against the
// collateral, so its flags matter far less. Those borrowers drop out of the
// tiles and the main table into an "Undrawn lines" section at the bottom,
// greyed. They are still scored and still one click away: a line can be drawn
// tomorrow, and the flags are what tell you whether you'd want it to be.
import { useState, useEffect, useRef } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate, localFilter } from "../../shared/format";
import { Card, ErrorMsg, SectionHeader, Spinner, Th, ThNum, Td, TdNum, Btn, SearchBar, FilterSelect, Modal } from "../../shared/ui";
import { useApp } from "../../shared/appContext";

const SEV = {
  High:      { fg: "#993C1D", bg: "#FAECE7", border: "#E8B4A8" },
  Medium:    { fg: "#BA7517", bg: "#FAEEDA", border: "#EBD3A3" },
  Pass:      { fg: "#0F6E56", bg: "#E1F5EE", border: "#B7E3D2" },
  "No data": { fg: "#6B6B6B", bg: "#F0F0EE", border: "#D8D8D4" },
  Low:       { fg: "#185FA5", bg: "#E6F1FB", border: "#B9D4EE" },
};

// The four outcomes, in the order a manager reads them: what tripped, what was
// looked at and came back clean, and what could not be looked at at all.
const OUTCOMES = ["High", "Medium", "Pass", "No data"];
const FAILING = ["High", "Medium"];

const SevChip = ({ s }) => {
  const c = SEV[s] || SEV.Low;
  return <span style={{ color: c.fg, background: c.bg, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{s}</span>;
};

const CoverageChip = ({ label, ok, title }) => (
  <span title={title} style={{
    fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4, whiteSpace: "nowrap",
    color: ok ? "#0F6E56" : "var(--color-text-secondary)",
    background: ok ? "#E1F5EE" : "var(--color-background-primary)",
    border: "0.5px solid " + (ok ? "#B7E3D2" : "var(--color-border-tertiary)"),
    opacity: ok ? 1 : 0.7,
  }}>{label}</span>
);

// Dependency-free sparkline. Nulls break the line rather than being drawn as
// zero — a gap in reporting must not read as a value of nothing.
const Spark = ({ values, w = 132, h = 34, color = B.cyan, invert = false }) => {
  const nums = values.map(v => (v === null || v === undefined || isNaN(Number(v))) ? null : Number(v));
  const present = nums.filter(v => v !== null);
  if (present.length < 2) return <div style={{ height: h, fontSize: 11, color: "var(--color-text-secondary)", display: "flex", alignItems: "center" }}>Not enough history</div>;

  const min = Math.min(...present), max = Math.max(...present);
  const span = (max - min) || 1;
  const x = i => (i / Math.max(nums.length - 1, 1)) * (w - 2) + 1;
  const y = v => h - 2 - ((v - min) / span) * (h - 4);

  const segs = [];
  let cur = [];
  nums.forEach((v, i) => {
    if (v === null) { if (cur.length) segs.push(cur); cur = []; return; }
    cur.push(`${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  });
  if (cur.length) segs.push(cur);

  const lastIdx = nums.reduce((acc, v, i) => (v !== null ? i : acc), -1);
  const first = present[0], last = present[present.length - 1];
  const rising = last > first;
  const stroke = (rising !== invert) ? "#993C1D" : "#0F6E56";

  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      {segs.map((pts, i) => (
        <polyline key={i} points={pts.join(" ")} fill="none" stroke={stroke} strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
      ))}
      {lastIdx >= 0 && <circle cx={x(lastIdx)} cy={y(nums[lastIdx])} r="2.5" fill={stroke} />}
    </svg>
  );
};

// ── Snapshot freshness ──────────────────────────────────────────────────────
// This page reads a nightly snapshot, not the live certificate tables — the
// view chain behind it cannot answer inside a request. So the age of the data
// is part of the data, and gets stated rather than implied.
const RefreshBanner = ({ refresh, onRebuild, canRebuild, busy }) => {
  if (!refresh) {
    return (
      <Card style={{ padding: "10px 14px", marginBottom: 14, background: "#FAEEDA", borderLeft: "3px solid #BA7517" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#BA7517", fontWeight: 600 }}>
            Insights have never been generated — every account will read as having no flags.
          </span>
          {canRebuild && <span style={{ marginLeft: "auto" }}><Btn variant="primary" onClick={onRebuild} disabled={busy}>{busy ? "Building…" : "Build now"}</Btn></span>}
        </div>
      </Card>
    );
  }

  const when = refresh.finishedat || refresh.startedat;
  const hours = when ? (Date.now() - new Date(when).getTime()) / 36e5 : null;
  const stale = hours !== null && hours > 36;
  const failed = refresh.status === 'Failed';
  const tone = failed ? { bg: "#FAECE7", fg: "#993C1D" } : stale ? { bg: "#FAEEDA", fg: "#BA7517" } : { bg: "var(--color-background-primary)", fg: "var(--color-text-secondary)" };

  const ago = hours === null ? "" :
    hours < 1 ? "less than an hour ago" :
    hours < 48 ? Math.round(hours) + " hours ago" :
    Math.round(hours / 24) + " days ago";

  return (
    <Card style={{ padding: "8px 14px", marginBottom: 14, background: tone.bg, borderLeft: "3px solid " + (failed ? "#993C1D" : stale ? "#BA7517" : B.cyan) }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: tone.fg }}>
          {failed
            ? <>Last rebuild <strong>failed</strong>{refresh.error ? " — " + refresh.error : ""}. Figures below are from the last successful run.</>
            : <>Rebuilt {ago}{refresh.seconds ? " in " + refresh.seconds + "s" : ""}. Certificates uploaded since then are not reflected.</>}
        </span>
        {canRebuild && <span style={{ marginLeft: "auto" }}><Btn onClick={onRebuild} disabled={busy}>{busy ? "Rebuilding…" : "Rebuild now"}</Btn></span>}
      </div>
    </Card>
  );
};

const REVOLVING = ft => /^revolving line of credit$/i.test((ft || "").trim());
const isUndrawn = bal => bal != null && Math.abs(bal) < 0.005;

const pct  = v => (v === null || v === undefined) ? "—" : (Number(v) * 100).toFixed(1) + "%";
const days = v => (v === null || v === undefined) ? "—" : Number(v).toFixed(0) + "d";
const usd0 = v => (v === null || v === undefined) ? "—" : "$" + Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Section heading for the metric strip. AR and AP answer different questions —
// what the borrower is owed versus what they owe — and mixing them in one grid
// made a six-tile wall with no reading order.
const MetricSection = ({ title, note }) => (
  <div style={{ margin: "4px 0 10px", display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-primary)",
                   textTransform: "uppercase", letterSpacing: "0.07em" }}>{title}</span>
    {note && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{note}</span>}
  </div>
);

// ── Metric strip on the detail view ─────────────────────────────────────────
const MetricCard = ({ label, value, series, note, invert }) => (
  <Card style={{ padding: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 4px" }}>{value}</div>
    {series ? <Spark values={series} invert={invert} /> : null}
    {note && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 6 }}>{note}</div>}
  </Card>
);

// ── Account detail ──────────────────────────────────────────────────────────
// ── Every check, not only the ones that tripped ─────────────────────────────
// The page used to show failures alone, which made two very different states
// look identical: a borrower where twenty-four checks ran and all passed, and a
// borrower where twenty-four checks could not run at all. Both showed nothing.
//
// Reads dbo.CollateralInsightCheck via the API. Each row carries its finding in
// words (the Finding column), so the failing rows need no separate cards: the
// table is the one place a flag is shown, and the counts tie by construction.
const ChecksTable = ({ checks }) => {
  const [only, setOnly] = useState(null);

  const counts = {};
  OUTCOMES.forEach(o => { counts[o] = checks.filter(c => c.severity === o).length; });
  const shown = only ? checks.filter(c => c.severity === only) : checks;

  return (
    <Card style={{ marginBottom: 22, overflowX: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>All checks</span>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {checks.length} run against this borrower's latest certificate
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {OUTCOMES.filter(o => counts[o] > 0).map(o => {
            const c = SEV[o];
            const active = only === o;
            return (
              <button key={o} type="button"
                // Clicking the active chip clears the filter, so there is always
                // a way back to the full list without hunting for a reset.
                onClick={() => setOnly(active ? null : o)}
                title={active ? "Show every check" : "Show only " + o}
                style={{
                  cursor: "pointer", fontSize: 11, fontWeight: 600, padding: "2px 8px",
                  borderRadius: 4, whiteSpace: "nowrap", color: c.fg,
                  background: active ? c.bg : "transparent",
                  border: "0.5px solid " + (active ? c.fg : c.border),
                }}>
                {counts[o]} {o === "Pass" ? "passed" : o === "No data" ? "no data" : o.toLowerCase()}
              </button>
            );
          })}
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <Th>Outcome</Th><Th>Source</Th><Th>Check</Th><Th>Reading</Th><Th>Finding</Th><Th>What it looks for</Th>
        </tr></thead>
        <tbody>
          {shown.map((c, i) => (
            <tr key={i}>
              <Td><SevChip s={c.severity} /></Td>
              <Td style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{c.source}</Td>
              <Td style={{ fontWeight: FAILING.includes(c.severity) ? 600 : 400 }}>{c.metric}</Td>
              {/* An em dash, not an empty cell. A check that could not run has
                  no reading, and a blank space reads as a value of nothing. */}
              <Td style={{ color: c.value_text ? "inherit" : "var(--color-text-secondary)" }}>
                {c.value_text || "—"}
              </Td>
              {/* The finding in words — what used to be the cards above this
                  table. Full colour on a failing row, quiet on a pass, a dash when
                  the check could not run (the view blanks its detail then). */}
              <Td style={{ fontSize: 12, whiteSpace: "normal", minWidth: 280, maxWidth: 560,
                           color: FAILING.includes(c.severity) ? "inherit" : "var(--color-text-secondary)" }}>
                {c.detail || "—"}
              </Td>
              <Td style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "normal", minWidth: 200 }}>
                {c.threshold}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {counts["No data"] > 0 && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 10 }}>
          {counts["No data"]} check{counts["No data"] === 1 ? "" : "s"} could not run for this borrower —
          the certificate does not carry the figures they need. That is not a pass.
        </div>
      )}
    </Card>
  );
};

const InsightDetail = ({ accountid, companyname, liveBal, onBack }) => {
  const { api, go } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getCollateralInsights", { accountid })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountid]);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  // `coverage`, not just `data`. Everything below reads cov.* directly, so a
  // response that arrives without it — a shape change, a half-deployed API, the
  // portfolio list returned where the detail was expected — takes the whole
  // page down with "Cannot read properties of undefined" and no way back. The
  // endpoint 404s when an account genuinely has no insights, and that is
  // already handled above as an error; this is for the case where it answers
  // 200 with something unexpected.
  if (!data || !data.coverage) return <ErrorMsg
    msg={"No insight detail came back for this account."} onRetry={load} />;

  const { coverage: cov, flags, checks, trend, contra, cert, refresh } = data;

  // The flags ARE the failing checks — dbo.vCollateralInsightFlags is defined as
  // the High/Medium subset of dbo.vCollateralInsightChecks. So when the checks
  // snapshot is deployed, the cards below are driven from it rather than from
  // the flags list, and the cards and the table cannot show different counts.
  //
  // `checks` comes back empty when dbo.CollateralInsightCheck has not been
  // created yet — the API answers with what it has rather than failing the
  // whole account view — so the page falls back to the flags it has always
  // read and simply does not offer the all-checks table.
  const allChecks = checks && checks.length ? checks : null;
  const failing = allChecks
    ? allChecks.filter(c => FAILING.includes(c.severity))
    : (flags || []);
  const latest = trend && trend.length ? trend[trend.length - 1] : {};
  const series = k => (trend || []).map(t => t[k]);

  // Same fallback BorrowingBaseDetail uses: an explicit URL if one was pasted
  // on upload, otherwise the Box file id the parser matched.
  const certHref = cert ? (cert.box_url || (cert.box_file_id ? "https://app.box.com/file/" + cert.box_file_id : null)) : null;

  // is_scored arrives from SQL as 0 or 1, not false/true. In JSX, `0 && <X/>`
  // renders a literal "0" on the page rather than nothing — so it gets coerced
  // to a real boolean once, here, instead of at each use site.
  const certScored = !!(cert && cert.is_scored);

  const linkStyle = {
    display: "inline-flex", alignItems: "center", padding: "6px 12px", borderRadius: 6,
    fontSize: 13, fontWeight: 500, textDecoration: "none",
    border: "0.5px solid " + B.cyan, color: B.cyanDark,
  };

  const notes = [];
  if (!cov.has_credits) notes.push("AR credits are not present in this borrower's file, so dilution cannot be measured. Values are suppressed rather than shown as zero.");
  if (!cov.has_ap)      notes.push("No A/P has been loaded for this borrower, so vendor-stretch and contra exposure are unavailable.");
  if (cov.ap_stale)     notes.push("A/P was reported historically but is missing from the most recent certificate.");

  return <>
    {/* Back on the left, actions on the right — the same row BorrowingBaseDetail
        uses, so "go back" sits in the same place across the Collateral app. */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
      <Btn onClick={onBack}>← All accounts</Btn>
      <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
        {cert && <Btn onClick={() => go("collateral-bbc", { bbid: cert.bbid })}>Open certificate</Btn>}
        {certHref
          ? <a href={certHref} target="_blank" rel="noopener noreferrer"
               title={cert.filename ? "Open " + cert.filename : "Open the signed workbook"}
               style={linkStyle}>View Online</a>
          : (cert
              ? <Btn disabled title="No document link is recorded on this certificate — add one from the Borrowing Bases list.">View Online</Btn>
              : null)}
        <Btn onClick={load}>Reload</Btn>
      </div>
    </div>

    <SectionHeader title={companyname || cov.companyname} actions={[
      <span key="asof" style={{ fontSize: 12, color: "var(--color-text-secondary)", alignSelf: "center" }}>
        {cert
          ? <>{certScored ? "Computed from the" : "Latest certificate"} {fmtDate(cert.asofdate)}{certScored ? " certificate" : ""} · {cov.bbc_count} on file</>
          : <>{cov.bbc_count} certificate{cov.bbc_count === 1 ? "" : "s"} on file</>}
      </span>,
    ]} />

    {certScored && cov.last_asofdate && String(cert.asofdate).slice(0, 10) !== String(cov.last_asofdate).slice(0, 10) && (
      <Card style={{ padding: "8px 14px", marginBottom: 14, background: "#FAEEDA", borderLeft: "3px solid #BA7517" }}>
        <span style={{ fontSize: 12, color: "#BA7517" }}>
          A newer certificate dated {fmtDate(cov.last_asofdate)} exists but is not yet reflected here.
          Everything below, and the certificate these buttons open, is as of {fmtDate(cert.asofdate)}.
        </span>
      </Card>
    )}

    <RefreshBanner refresh={refresh} canRebuild={false} />

    {isUndrawn(liveBal) && (
      <Card style={{ padding: "8px 14px", marginBottom: 14, background: "var(--color-background-secondary)", borderLeft: "3px solid #888780" }}>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          <strong>Undrawn line.</strong> The revolving line is at $0 today, so nothing is advanced against this collateral
          and no BBC is required. The flags below are low priority until the line is drawn again.
        </span>
      </Card>
    )}

    {notes.length > 0 && (
      <Card style={{ padding: "10px 14px", marginBottom: 16, background: "var(--color-background-primary)", borderLeft: "3px solid " + B.cyan }}>
        {notes.map((n, i) => (
          <div key={i} style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: i < notes.length - 1 ? 6 : 0 }}>{n}</div>
        ))}
      </Card>
    )}

    {/* ── Receivable measures ── */}
    <MetricSection title="Receivable measures" note="What the borrower is owed" />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 22 }}>
      {/* Dilution and DSO are the trailing 90-day figures — the same numbers,
          by the same definition, as the Collateral Portfolio page and the AR
          Quality card (dbo.vARDilutionWindow mirrors arMetrics.js). The
          z-score flags still run on each certificate's own period. */}
      <MetricCard label="Dilution (90d)"
        value={cov.has_credits ? pct(latest.dilution_90d) : "Not reported"}
        series={cov.has_credits ? series("dilution_90d") : null}
        note={cov.has_credits ? "(Credits + discounts + write-offs) ÷ gross sales, trailing 90 days" : "No AR credits in the file"} />
      <MetricCard label="DSO (90d)" value={days(latest.dso_90d)} series={series("dso_90d")}
        note="Ending A/R ÷ average daily gross sales, trailing 90 days" />
      <MetricCard label="Aging roll rate" value={pct(latest.roll_rate)} series={series("roll_rate")}
        note="Open AR that aged into a worse bucket" />
      <MetricCard label="Ineligibles" value={pct(latest.ineligible_pct)} series={series("ineligible_pct")}
        note="Share of gross AR" />
    </div>

    {/* ── Payable measures ── */}
    {/* Stated as absent rather than silently dropped — an empty space reads as
        "nothing wrong here", which is the opposite of "we were never sent it". */}
    <MetricSection title="Payable measures"
      note={cov.has_ap ? "What the borrower owes" : "No A/P has been loaded for this borrower"} />
    {cov.has_ap ? (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 20 }}>
        <MetricCard label="AP days past due" value={days(latest.wtd_avg_dpd)} series={series("wtd_avg_dpd")}
          note="Balance-weighted, respects vendor terms" />
        <MetricCard label="AP over 90 days" value={pct(latest.ap_pct_over90)} series={series("ap_pct_over90")}
          note="Share of AP more than 90 days past due" />
        <MetricCard label="AP vs AR" value={pct(latest.ap_to_ar)} series={series("ap_to_ar")}
          note="AP as a share of open AR" />
      </div>
    ) : (
      <Card style={{ padding: "12px 14px", marginBottom: 20, fontSize: 12, color: "var(--color-text-secondary)" }}>
        Vendor stretch and contra exposure need an A/P waterfall in the certificate. Most borrowers
        do not send one, so this is normal rather than a problem — but it does mean this borrower is
        being watched on the receivable side only.
      </Card>
    )}

    {/* ── What tripped ──
        Only when there is no all-checks table. With it, every finding's
        sentence is in the table's Finding column, so the cards were the same
        rows said twice. Without it (CollateralInsightCheck not deployed) the
        cards are the only place the flags appear. */}
    {!allChecks && <div style={{ marginBottom: 20 }}>
      {failing.length === 0
        ? <Card style={{ padding: 16, fontSize: 13, color: "var(--color-text-secondary)" }}>
            {allChecks
              ? "Nothing tripped on the latest certificate. The table below says which checks that covers, and which of them could not run."
              : "No flags on the latest certificate. Scoring needs at least four prior certificates before it will raise anything."}
          </Card>
        : failing.map((f, i) => {
            const c = SEV[f.severity] || SEV.Low;
            return (
              <Card key={i} style={{ padding: "12px 14px", marginBottom: 8, borderLeft: "3px solid " + c.fg }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <SevChip s={f.severity} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{f.metric}</span>
                  <span style={{ fontSize: 11, color: "var(--color-text-secondary)", background: "var(--color-background-primary)", padding: "1px 6px", borderRadius: 4 }}>{f.source}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(f.asofdate)}</span>
                </div>
                <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 6 }}>{f.detail}</div>
              </Card>
            );
          })}
    </div>}

    {/* ── Every check ── */}
    {allChecks && <ChecksTable checks={allChecks} />}

    {/* ── Contra ── */}
    {contra && contra.length > 0 && (
      <Card style={{ overflowX: "auto" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
          Contra exposure
          <span style={{ fontWeight: 400, fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 8 }}>
            names that are both a debtor and a vendor — they can set off
          </span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead><tr>
            <Th>Name</Th><ThNum>A/R Open</ThNum><ThNum>A/P Open</ThNum>
            <ThNum>Offset at Risk</ThNum><ThNum>Inside Eligible A/R</ThNum>
          </tr></thead>
          <tbody>
            {contra.map((c, i) => (
              <tr key={i}>
                <Td>{c.consolidatedname}</Td>
                <TdNum>{usd0(c.ar_open)}</TdNum>
                <TdNum>{usd0(c.ap_open)}</TdNum>
                <TdNum>{usd0(c.contra_exposure)}</TdNum>
                <TdNum style={{ fontWeight: 500, color: Number(c.contra_in_eligible) > 0 ? "#993C1D" : "inherit" }}>
                  {usd0(c.contra_in_eligible)}
                </TdNum>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    )}
  </>;
};

// ── Thresholds window ───────────────────────────────────────────────────────
// Every check and the numbers it flags at (dbo.CollateralInsightThreshold).
// Everyone who can see Insights can read them; only an administrator can
// change them — api/saveInsightThreshold refuses anyone else on the server.
// Percentages are stored as fractions and shown as percents here.
//
// Saving does not rebuild: Insights is a snapshot, so the window says the
// change is pending and offers Rebuild (which the rebuild endpoint also
// limits to administrators).
const UNIT_LABEL = { pct: "%", usd: "$", days: "days", count: "count", z: "z" };
const toShown = (unit, v) => v == null ? "" : unit === "pct" ? String(+(Number(v) * 100).toFixed(4)) : String(+Number(v).toFixed(4));
const toStored = (unit, s) => unit === "pct" ? Number(s) / 100 : Number(s);
const fmtThr = (unit, v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (unit === "pct") return +(n * 100).toFixed(2) + "%";
  if (unit === "usd") return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (unit === "days") return +n.toFixed(2) + " days";
  if (unit === "z") return "z " + +n.toFixed(2);
  return String(+n.toFixed(2));
};

const ThresholdsModal = ({ onClose, onRebuild }) => {
  const { api, role } = useApp();
  const isAdmin = !!(role && role.isAdmin);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [edits, setEdits] = useState({});     // { metric: { field: "typed" } }
  const [busy, setBusy] = useState(null);
  const [pending, setPending] = useState(false);

  const load = () => api.get("getInsightThresholds").then(d => setRows(Array.isArray(d) ? d : [])).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const FIELDS = [["medium_value", "unit"], ["high_value", "unit"], ["medium_value2", "unit2"], ["high_value2", "unit2"]];
  const val = (r, f, u) => (edits[r.metric] && edits[r.metric][f] !== undefined) ? edits[r.metric][f] : toShown(r[u], r[f]);
  const dirty = r => FIELDS.some(([f, u]) => r[f] != null && edits[r.metric] && edits[r.metric][f] !== undefined
                                              && edits[r.metric][f] !== toShown(r[u], r[f]));
  const save = async (r, body) => {
    setBusy(r.metric); setErr(null);
    try {
      const res = await api.post("saveInsightThreshold", { metric: r.metric, ...body });
      setEdits(e => { const n = { ...e }; delete n[r.metric]; return n; });
      if (res && res.changed) setPending(true);
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(null);
  };
  const saveValues = r => {
    const body = {};
    FIELDS.forEach(([f, u]) => {
      if (r[f] == null) return;
      const typed = val(r, f, u);
      if (typed.trim() === "" || !Number.isFinite(Number(typed))) { body[f] = ""; return; }   // the server explains
      body[f] = toStored(r[u], typed);
    });
    save(r, body);
  };

  const inp = { width: 74, fontSize: 12, padding: "3px 6px", border: "1px solid #c8cdd2", borderRadius: 4, textAlign: "right" };
  const cell = (r, f, u) => {
    if (r[f] == null) return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
    if (!isAdmin) return fmtThr(r[u], r[f]);
    return (
      <span style={{ display: "inline-flex", gap: 4, alignItems: "center", whiteSpace: "nowrap" }}>
        {r[u] === "usd" && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>$</span>}
        <input value={val(r, f, u)} style={inp} disabled={busy === r.metric}
               onChange={e => setEdits(x => ({ ...x, [r.metric]: { ...(x[r.metric] || {}), [f]: e.target.value } }))} />
        {r[u] !== "usd" && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{UNIT_LABEL[r[u]] || ""}</span>}
      </span>
    );
  };

  const groups = [];
  (rows || []).forEach(r => {
    const g = groups.find(x => x.source === r.source);
    if (g) g.rows.push(r); else groups.push({ source: r.source, rows: [r] });
  });
  const th = { textAlign: "left", fontSize: 11, color: B.cyanDark, padding: "6px 8px", borderBottom: "1.5px solid " + B.cyanLight, whiteSpace: "nowrap" };

  return <Modal title="Insights thresholds" onClose={onClose} wide>
    <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <span>{isAdmin
        ? "Change where a check flags Medium or High, or switch a check off. Insights is a snapshot — rebuild when you are done."
        : "What each check flags at. Only an administrator can change these."}</span>
      {isAdmin && pending && (
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#7A5200" }}>Changed — rebuild to apply</span>
          <Btn variant="primary" onClick={() => { onRebuild(); onClose(); }}>Rebuild now</Btn>
        </span>
      )}
    </div>
    {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
    {!rows ? (!err && <Spinner />) : (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={th}>Check</th><th style={th}>Medium</th><th style={th}>High</th><th style={th}>Notes</th><th style={th}>On</th>
        </tr></thead>
        <tbody>
          {groups.map(g => [
            <tr key={"g" + g.source}><td colSpan={5} style={{ padding: "12px 8px 4px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                                                             letterSpacing: "0.06em", color: "var(--color-text-secondary)" }}>
              {g.source === "All" ? "Shared" : g.source}</td></tr>,
            ...g.rows.map(r => (
              <tr key={r.metric} style={{ opacity: r.isactive ? 1 : 0.55 }}>
                <Td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{r.metric}</Td>
                {r.kind === "z" || r.kind === "onoff"
                  ? <Td colSpan={2} style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                      {r.kind === "z" ? "Uses the shared own-history z-score" : "Fixed rule — on or off only"}</Td>
                  : <>
                      <Td>{cell(r, "medium_value", "unit")}
                        {r.medium_value2 != null && <div style={{ marginTop: 4 }}>{cell(r, "medium_value2", "unit2")}</div>}</Td>
                      <Td>{cell(r, "high_value", "unit")}
                        {r.high_value2 != null && <div style={{ marginTop: 4 }}>{cell(r, "high_value2", "unit2")}</div>}</Td>
                    </>}
                <Td style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "normal", maxWidth: 380 }}>
                  {r.direction === "below" && <strong>Flags when the figure falls BELOW. </strong>}
                  {r.direction === "atleast" && <strong>Flags at or above. </strong>}
                  {r.note}
                  {r.updatedat && <div style={{ marginTop: 2 }}>Last changed {fmtDate(r.updatedat)}{r.updatedby ? " by " + r.updatedby : ""}</div>}
                  {isAdmin && dirty(r) && <div style={{ marginTop: 6 }}>
                    <Btn variant="primary" disabled={busy === r.metric} onClick={() => saveValues(r)}>{busy === r.metric ? "…" : "Save"}</Btn></div>}
                </Td>
                <Td>
                  {r.kind === "shared" ? <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Always</span>
                    : isAdmin
                      ? <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer", whiteSpace: "nowrap" }}>
                          <input type="checkbox" checked={!!r.isactive} disabled={busy === r.metric}
                                 onChange={e => save(r, { isactive: e.target.checked })} />
                          {r.isactive ? "On" : "Off"}
                        </label>
                      : (r.isactive ? "On" : "Off")}
                </Td>
              </tr>
            )),
          ])}
        </tbody>
      </table>
    )}
  </Modal>;
};

// ── Portfolio list ──────────────────────────────────────────────────────────
export const CollateralInsights = () => {
  const { api, role } = useApp();
  const [rows, setRows] = useState([]);
  const [bals, setBals] = useState(null);      // accountid -> live revolver BS balance; null = unavailable
  const [refresh, setRefresh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState("");
  const [sel, setSel] = useState(null);   // { accountid, companyname }
  const [busy, setBusy] = useState(false);
  const [rebuildErr, setRebuildErr] = useState(null);
  const [showThr, setShowThr] = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getCollateralInsights")
      .then(d => {
        // The endpoint used to return a bare array and now returns { rows, refresh }.
        setRows(Array.isArray(d) ? d : (d && Array.isArray(d.rows) ? d.rows : []));
        setRefresh(Array.isArray(d) ? null : (d && d.refresh) || null);
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Live revolver balances, fetched alongside and kept apart from load() so the
  // rebuild polling below doesn't re-pull every loan every five seconds. If it
  // fails the page behaves exactly as before: every borrower in the main table.
  useEffect(() => {
    api.get("getLoans")
      .then(l => {
        const m = {};
        (Array.isArray(l) ? l : []).filter(x => REVOLVING(x.facilitytype)).forEach(x => {
          const k = String(x.accountid);
          m[k] = (m[k] || 0) + (parseFloat(x.bsloanbalanceeod) || 0);
        });
        setBals(m);
      })
      .catch(() => setBals(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const balOf = id => (bals && bals[String(id)] != null ? bals[String(id)] : null);

  // ── THE REBUILD IS WATCHED, NOT AWAITED ───────────────────────────────────
  //
  // It takes about ninety seconds, and Azure Static Web Apps kills any response
  // proxied to a managed function at forty-five. Waiting for the POST to come
  // back therefore CANNOT work on the deployed site: the rebuild finishes
  // perfectly well, and the browser is handed a 500 halfway through it. That is
  // what this page used to report, every single time, while the run log said
  // 'Succeeded'.
  //
  // So the POST only starts the run. The status comes from the run log, which
  // getCollateralInsights already returns on every call — the same `refresh`
  // object this page has always displayed. Polling it is a reload on a timer.
  const rebuild = () => {
    setBusy(true); setRebuildErr(null);
    api.post("refreshCollateralInsights", {})
      .then(r => {
        if (r && r.alreadyRunning) setRebuildErr(r.message || "A rebuild is already running.");
        // Whether it started now or was already going, the thing to do is the
        // same: watch the run log until it stops saying Running.
        pollUntilDone();
      })
      .catch(e => { setRebuildErr(e.message); setBusy(false); });
  };

  // Polling stops on three things: the run finishing, the component unmounting,
  // and a ceiling. The ceiling matters — a refresh that dies without updating
  // its row would otherwise leave this asking forever, and an interval that
  // outlives the page it belongs to is how a closed tab keeps hitting the API.
  const pollRef = useRef(null);
  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPolling, []);

  const POLL_EVERY_MS = 5000;
  const GIVE_UP_AFTER_MS = 6 * 60 * 1000;   // four times the ninety seconds it takes

  const pollUntilDone = () => {
    stopPolling();
    const startedAt = Date.now();
    pollRef.current = setInterval(() => {
      api.get("getCollateralInsights")
        .then(d => {
          const r = Array.isArray(d) ? null : (d && d.refresh) || null;
          setRefresh(r);
          if (r && r.status !== "Running") {
            // Finished — take the rows that came with this same response rather
            // than asking again for what we were just handed.
            setRows(Array.isArray(d) ? d : (d && Array.isArray(d.rows) ? d.rows : []));
            stopPolling(); setBusy(false);
            // A failure needs no second announcement here. RefreshBanner
            // already reports it, with the error and the sentence that
            // matters — that the figures on screen are the last good run's.
            // Repeating it as a red box below would say the same thing twice
            // and leave the reader working out whether they are two problems.
          } else if (Date.now() - startedAt > GIVE_UP_AFTER_MS) {
            stopPolling(); setBusy(false);
            setRebuildErr("The rebuild has been running for over six minutes. It may still finish — "
                        + "reload to check — but something is wrong if it does not.");
          }
        })
        .catch(() => { /* a blip mid-rebuild is not worth stopping for; the next tick retries */ });
    }, POLL_EVERY_MS);
  };

  if (sel) return <InsightDetail accountid={sel.accountid} companyname={sel.companyname} liveBal={balOf(sel.accountid)} onBack={() => { setSel(null); load(); }} />;
  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  let shown = localFilter(rows, q, ["companyname", "top_flags"]);
  if (sev === "High")   shown = shown.filter(r => r.high_count > 0);
  if (sev === "Medium") shown = shown.filter(r => r.high_count === 0 && r.medium_count > 0);
  if (sev === "Clear")  shown = shown.filter(r => r.total_flags === 0);

  // Drawn lines are the work; undrawn ones are set aside below.
  const drawn       = rows.filter(r => !isUndrawn(balOf(r.accountid)));
  const shownDrawn  = shown.filter(r => !isUndrawn(balOf(r.accountid)));
  const shownIdle   = shown.filter(r => isUndrawn(balOf(r.accountid)));
  const idleTotal   = rows.length - drawn.length;
  const idleFlagged = rows.filter(r => isUndrawn(balOf(r.accountid)) && r.total_flags > 0).length;

  const highAccts = drawn.filter(r => r.high_count > 0).length;
  const medAccts  = drawn.filter(r => r.high_count === 0 && r.medium_count > 0).length;
  const clear     = drawn.filter(r => r.total_flags === 0).length;

  const Row = ({ r, idle }) => (
    <tr style={{ cursor: "pointer", opacity: idle ? 0.6 : 1 }}
      onClick={() => setSel({ accountid: r.accountid, companyname: r.companyname })}
      onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
      onMouseLeave={e => e.currentTarget.style.background = ""}>
      <Td style={{ fontWeight: 500 }}>{r.companyname}</Td>
      <TdNum style={{ color: idle ? "var(--color-text-secondary)" : "inherit" }}>
        {balOf(r.accountid) == null ? "—" : fmt(balOf(r.accountid))}</TdNum>
      <Td style={{ color: "var(--color-text-secondary)", fontSize: 12 }}>{fmtDate(r.last_asofdate)}</Td>
      <TdNum>{r.high_count > 0 ? <SevChip s="High" /> : <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</TdNum>
      <TdNum>{r.medium_count > 0 ? <span style={{ fontWeight: 600, color: "#BA7517" }}>{r.medium_count}</span> : <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</TdNum>
      <Td style={{ fontSize: 12, color: "var(--color-text-secondary)", maxWidth: 380, whiteSpace: "normal" }}>
        {r.top_flags || <span style={{ color: "#0F6E56" }}>Clear</span>}
      </Td>
      <Td>
        <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
          <CoverageChip label="AR"       ok={!!r.has_ar}      title="AR waterfall loaded" />
          <CoverageChip label="Credits"  ok={!!r.has_credits} title="AR credits present — dilution measurable" />
          <CoverageChip label="AP"       ok={!!r.has_ap}      title="AP waterfall loaded" />
        </span>
      </Td>
    </tr>
  );
  const Head = () => (
    <thead><tr>
      <Th>Company</Th>
      <ThNum>BS Loan Balance</ThNum>
      <Th>Latest Cert</Th>
      <ThNum>High</ThNum>
      <ThNum>Medium</ThNum>
      <Th>Flags</Th>
      <Th>Data</Th>
    </tr></thead>
  );

  const Tile = ({ label, n, color }) => (
    <Card style={{ padding: "12px 16px", flex: "1 1 140px" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 500, color, marginTop: 4 }}>{n}</div>
    </Card>
  );

  return <>
    <SectionHeader title="Insights" count={drawn.length} actions={[
      <SearchBar key="q" value={q} onChange={setQ} placeholder="Search company or flag…" />,
      <FilterSelect key="sev" label="Severity" value={sev} onChange={setSev} allLabel="All accounts"
        options={[{ value: "High", label: "Has high" }, { value: "Medium", label: "Medium only" }, { value: "Clear", label: "No flags" }]} />,
      <Btn key="thr" onClick={() => setShowThr(true)}>Thresholds</Btn>,
      <Btn key="refresh" onClick={load}>Reload</Btn>,
    ]} />

    {showThr && <ThresholdsModal onClose={() => setShowThr(false)} onRebuild={rebuild} />}

    <RefreshBanner refresh={refresh} onRebuild={rebuild} busy={busy}
      canRebuild={!!(role && role.canManageUsers)} />

    {rebuildErr && <div style={{ marginBottom: 14 }}><ErrorMsg msg={rebuildErr} /></div>}

    <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
      <Tile label="High severity" n={highAccts} color="#993C1D" />
      <Tile label="Medium only"   n={medAccts}  color="#BA7517" />
      <Tile label="No flags"      n={clear}     color="#0F6E56" />
      {idleTotal > 0 && <Tile label="Undrawn ($0 balance)" n={idleTotal} color="var(--color-text-secondary)" />}
    </div>

    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <Head />
        <tbody>
          {shownDrawn.length === 0
            ? <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
                {rows.length === 0 ? "No borrowing bases loaded yet." : "No drawn lines match this filter."}
              </td></tr>
            : shownDrawn.map(r => <Row key={r.accountid} r={r} />)}
        </tbody>
      </table>
    </Card>

    {idleTotal > 0 && (
      <Card style={{ overflowX: "auto", marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Undrawn lines</span>
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            {idleTotal} borrower{idleTotal === 1 ? "" : "s"} with the revolving line at $0 today
            {idleFlagged ? ` · ${idleFlagged} with flags` : ""} — nothing advanced, so lower priority.
          </span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
          <Head />
          <tbody>
            {shownIdle.length === 0
              ? <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>No undrawn lines match this filter.</td></tr>
              : shownIdle.map(r => <Row key={r.accountid} r={r} idle />)}
          </tbody>
        </table>
      </Card>
    )}
  </>;
};
